import { useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';

import type { StorefrontProductSummary } from '@/features/storefront/api/storefront';
import { useStorefrontData, useStorefrontScope } from '@/features/storefront/data/context';
import { useLocalList } from '@/features/storefront/wishlist/localList';
import { recentlyViewed, RECENTLY_VIEWED_MAX } from '@/features/storefront/wishlist/recentlyViewed';

/**
 * The products on this browser's "recently viewed" trail, newest first, as
 * listing cards - without the one on screen (`excludeSlug`), at most `limit`.
 *
 * One listing request for the whole trail, by id. A product the shop has
 * since hidden does not come back, so it is not shown. `products` is
 * undefined while that request is in flight and an empty list when there is
 * nothing to show, which is the section's cue to leave the page.
 */
export function useRecentlyViewedProducts(
  limit: number,
  excludeSlug: string | null | undefined,
  enabled = true,
): { products: StorefrontProductSummary[] | undefined } {
  const source = useStorefrontData();
  const scope = useStorefrontScope();
  const trail = useLocalList(recentlyViewed(source.key));
  // Sorted for the cache key: one request per set, whatever order it is in.
  const ids = useMemo(() => trail.slice(0, RECENTLY_VIEWED_MAX).sort(), [trail]);
  const wanted = enabled && ids.length > 0;

  const query = useQuery({
    queryKey: [...scope, 'products', 'by-id', ids],
    queryFn: () => source.products({ ids, size: ids.length }),
    enabled: wanted,
  });

  const products = useMemo(() => {
    if (!wanted) return [];
    if (!query.data) return query.isError ? [] : undefined;
    const byId = new Map(query.data.items.map((product) => [product.id, product]));
    return trail
      .flatMap((id) => byId.get(id) ?? [])
      .filter((product) => product.slug !== excludeSlug)
      .slice(0, limit);
  }, [wanted, query.data, query.isError, trail, excludeSlug, limit]);

  return { products };
}
